/**
 * Componente memoizado para renderizar um único segmento de transcrição
 * Otimizado para performance com React.memo e callbacks estáveis
 */

import React, { memo, useCallback, useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Badge,
  TextField,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import HighlightIcon from '@mui/icons-material/Highlight'
import EditNoteIcon from '@mui/icons-material/EditNote'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import { TranscriptSegment, Annotation, SegmentHighlight, Bookmark, AnnotationColor, AnnotationCategory } from '../../../types/transcription'
import { formatTimestamp } from '../../../services/transcriptionAnalysis'

// Cores para anotações
export const ANNOTATION_COLORS: Record<AnnotationColor, { bg: string; border: string; label: string }> = {
  yellow: { bg: '#fff9c4', border: '#e6c84a', label: 'Amarelo' },
  green: { bg: '#c8e6c9', border: '#81c784', label: 'Verde' },
  blue: { bg: '#bbdefb', border: '#64b5f6', label: 'Azul' },
  red: { bg: '#ffcdd2', border: '#ef9a9a', label: 'Vermelho' },
  purple: { bg: '#e1bee7', border: '#ce93d8', label: 'Roxo' }
}

// Categorias de anotação
export const ANNOTATION_CATEGORIES: Record<AnnotationCategory, { icon: string; label: string }> = {
  note: { icon: '📝', label: 'Nota' },
  question: { icon: '❓', label: 'Questão' },
  important: { icon: '⚠️', label: 'Importante' },
  todo: { icon: '✅', label: 'A Fazer' },
  reference: { icon: '🔗', label: 'Referência' }
}

export interface SegmentAnnotations {
  notes: Annotation[]
  highlights: SegmentHighlight[]
  bookmarks: Bookmark[]
}

export interface TranscriptSegmentItemProps {
  segment: TranscriptSegment
  originalIndex: number
  isSearchMatch: boolean
  isCurrentMatch: boolean
  isHighlighted: boolean
  isActive?: boolean
  annotations: SegmentAnnotations
  speakerColor: string
  speakerName: string
  canEdit: boolean
  renderedText: React.ReactNode
  style?: React.CSSProperties
  onSegmentSave?: (segmentIndex: number, text: string, speaker: string) => Promise<boolean>
  onContextMenu: (event: React.MouseEvent, segmentIndex: number) => void
  onOpenAnnotationDialog: (segmentIndex: number, editingId?: string) => void
  onToggleHighlight: (segmentIndex: number) => void
  onToggleBookmark: (segmentIndex: number) => void
  onDeleteAnnotation: (annotationId: string) => void
  /** Callback para pausar áudio ao iniciar edição */
  onPauseForAction?: () => void
  /** Callback para retomar áudio após concluir/cancelar edição */
  onResumeAfterAction?: () => void
  /** Callback para reproduzir apenas o trecho deste segmento */
  onPlaySegment?: (startTime: number, endTime: number) => void
  /** Se este segmento está sendo reproduzido em modo segmento */
  isSegmentPlaying?: boolean
  /** Callback para parar reprodução de segmento */
  onStopSegmentPlayback?: () => void
  /** Se a mídia está disponível para reprodução */
  mediaAvailable?: boolean
}

const TranscriptSegmentItem = memo(function TranscriptSegmentItem({
  segment,
  originalIndex,
  isSearchMatch,
  isCurrentMatch,
  isHighlighted,
  isActive = false,
  annotations,
  speakerColor,
  speakerName,
  canEdit,
  renderedText,
  style,
  onSegmentSave,
  onContextMenu,
  onOpenAnnotationDialog,
  onToggleHighlight,
  onToggleBookmark,
  onDeleteAnnotation,
  onPauseForAction,
  onResumeAfterAction,
  onPlaySegment,
  isSegmentPlaying = false,
  onStopSegmentPlayback,
  mediaAvailable = false
}: TranscriptSegmentItemProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Local editing state
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [localText, setLocalText] = useState(segment.text)
  const [localSpeaker, setLocalSpeaker] = useState(speakerName)
  const textFieldRef = useRef<HTMLTextAreaElement>(null)

  // Sync local values when segment changes externally
  useEffect(() => {
    if (!isEditing) {
      setLocalText(segment.text)
      setLocalSpeaker(speakerName)
    }
  }, [segment.text, speakerName, isEditing])

  // Focus text field when entering edit mode
  useEffect(() => {
    if (isEditing && textFieldRef.current) {
      textFieldRef.current.focus()
      textFieldRef.current.setSelectionRange(textFieldRef.current.value.length, textFieldRef.current.value.length)
    }
  }, [isEditing])

  const hasHighlight = annotations.highlights.length > 0
  const hasBookmark = annotations.bookmarks.length > 0
  const hasNotes = annotations.notes.length > 0
  const highlightColor = hasHighlight ? annotations.highlights[0].color : null

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    onContextMenu(e, originalIndex)
  }, [onContextMenu, originalIndex])

  const handleAddNote = useCallback(() => {
    onOpenAnnotationDialog(originalIndex)
  }, [onOpenAnnotationDialog, originalIndex])

  const handleToggleHighlight = useCallback(() => {
    onToggleHighlight(originalIndex)
  }, [onToggleHighlight, originalIndex])

  const handleToggleBookmark = useCallback(() => {
    onToggleBookmark(originalIndex)
  }, [onToggleBookmark, originalIndex])

  const handlePlaySegment = useCallback(() => {
    if (isSegmentPlaying) {
      onStopSegmentPlayback?.()
    } else {
      const start = parseFloat(segment.start_time)
      const end = parseFloat(segment.end_time)
      if (!isNaN(start) && !isNaN(end)) {
        onPlaySegment?.(start, end)
      }
    }
  }, [isSegmentPlaying, onStopSegmentPlayback, onPlaySegment, segment.start_time, segment.end_time])

  const handleStartEdit = useCallback(() => {
    setLocalText(segment.text)
    setLocalSpeaker(speakerName)
    setIsEditing(true)
    onPauseForAction?.()
  }, [segment.text, speakerName, onPauseForAction])

  const handleCancelEdit = useCallback(() => {
    setLocalText(segment.text)
    setLocalSpeaker(speakerName)
    setIsEditing(false)
    onResumeAfterAction?.()
  }, [segment.text, speakerName, onResumeAfterAction])

  const handleConfirmEdit = useCallback(async () => {
    if (onSegmentSave) {
      setIsSaving(true)
      try {
        const success = await onSegmentSave(originalIndex, localText, localSpeaker)
        if (success) {
          setIsEditing(false)
        }
      } finally {
        setIsSaving(false)
        onResumeAfterAction?.()
      }
    }
  }, [onSegmentSave, originalIndex, localText, localSpeaker, onResumeAfterAction])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancelEdit()
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleConfirmEdit()
    }
  }, [handleCancelEdit, handleConfirmEdit])

  return (
    <div style={{ ...style, paddingRight: isMobile ? 4 : 8, paddingBottom: isMobile ? 4 : 8 }} data-segment-index={originalIndex}>
      <Paper
        elevation={isActive || isCurrentMatch || isHighlighted ? 3 : 0}
        onContextMenu={handleContextMenu}
        sx={{
          p: { xs: 1.25, sm: 2 },
          height: 'auto',
          minHeight: 'calc(100% - 8px)',
          boxSizing: 'border-box',
          borderLeft: '3px solid',
          borderLeftColor: hasHighlight && highlightColor
            ? ANNOTATION_COLORS[highlightColor].border
            : isActive
            ? '#4caf50'
            : isHighlighted
            ? '#90caf9'
            : isCurrentMatch
            ? '#ffb74d'
            : isSearchMatch
            ? '#ffd54f'
            : segment.speaker
            ? speakerColor
            : '#e0e0e0',
          bgcolor: hasHighlight && highlightColor
            ? ANNOTATION_COLORS[highlightColor].bg
            : isActive
            ? '#e8f5e9'
            : isHighlighted
            ? '#e3f2fd'
            : isCurrentMatch
            ? '#fff8e1'
            : isSearchMatch
            ? '#fffde7'
            : '#fff',
          transition: 'all 0.3s ease',
          boxShadow: isActive
            ? '0 0 12px rgba(76, 175, 80, 0.35)'
            : isHighlighted ? '0 0 8px rgba(25, 118, 210, 0.4)' : undefined,
          position: 'relative',
          '&:hover': {
            bgcolor: hasHighlight && highlightColor
              ? ANNOTATION_COLORS[highlightColor].bg
              : isActive ? '#c8e6c9'
              : isHighlighted ? '#e3f2fd' : '#f5f5f5',
            '& .annotation-actions': {
              opacity: 1
            }
          },
          // Override para WordPress: background-color e border-left podem ser sobrescritos
          // pelos estilos globais do tema WP. Usamos o ID raiz com !important para garantir
          // que os estados visuais (ativo, busca, destaque) sejam sempre respeitados.
          '#monkee-file-transcript-client &': {
            backgroundColor: (hasHighlight && highlightColor
              ? ANNOTATION_COLORS[highlightColor].bg
              : isActive
              ? '#e8f5e9'
              : isHighlighted
              ? '#e3f2fd'
              : isCurrentMatch
              ? '#fff8e1'
              : isSearchMatch
              ? '#fffde7'
              : '#ffffff') + ' !important',
            borderLeft: '3px solid !important',
            borderLeftColor: (hasHighlight && highlightColor
              ? ANNOTATION_COLORS[highlightColor].border
              : isActive
              ? '#4caf50'
              : isHighlighted
              ? '#90caf9'
              : isCurrentMatch
              ? '#ffb74d'
              : isSearchMatch
              ? '#ffd54f'
              : (segment.speaker ? speakerColor : '#e0e0e0')) + ' !important',
            boxShadow: (isActive
              ? '0 0 12px rgba(76, 175, 80, 0.35) !important'
              : isHighlighted
              ? '0 0 8px rgba(25, 118, 210, 0.4) !important'
              : 'none !important'),
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 2 } }}>
          {/* Timestamp + Play */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <Chip
              label={formatTimestamp(segment.start_time)}
              size="small"
              sx={{
                fontFamily: 'monospace',
                bgcolor: '#f5f5f5',
                color: '#666',
                fontSize: { xs: '0.65rem', sm: '0.8125rem' },
                height: { xs: 22, sm: 24 },
                '& .MuiChip-label': {
                  px: { xs: 0.75, sm: 1 }
                }
              }}
            />
            {onPlaySegment && mediaAvailable && (
              <Tooltip title={isSegmentPlaying ? "Parar segmento" : "Reproduzir segmento"} disableHoverListener={isMobile}>
                <IconButton
                  size="small"
                  onClick={handlePlaySegment}
                  sx={{
                    p: { xs: 0.15, sm: 0.25 },
                    color: isSegmentPlaying ? '#f44336' : '#4caf50',
                    transition: 'color 0.2s',
                    '&:hover': {
                      bgcolor: isSegmentPlaying ? 'rgba(244, 67, 54, 0.08)' : 'rgba(76, 175, 80, 0.08)'
                    }
                  }}
                >
                  {isSegmentPlaying
                    ? <StopIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    : <PlayArrowIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  }
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Conteúdo */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {segment.speaker && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <PersonIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: speakerColor }} />
                {isEditing ? (
                  <TextField
                    value={localSpeaker}
                    onChange={(e) => setLocalSpeaker(e.target.value)}
                    onKeyDown={handleKeyDown}
                    size="small"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 600,
                        color: speakerColor,
                        py: 0,
                        '& input': { py: 0.25, px: 0.5 }
                      },
                      maxWidth: 200
                    }}
                  />
                ) : (
                  <Typography
                    variant="subtitle2"
                    sx={{ color: speakerColor, fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {speakerName}
                  </Typography>
                )}
              </Box>
            )}
            {isEditing ? (
              <Box>
                <TextField
                  inputRef={textFieldRef}
                  value={localText}
                  onChange={(e) => setLocalText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  multiline
                  fullWidth
                  minRows={2}
                  maxRows={10}
                  size="small"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      lineHeight: { xs: 1.5, sm: 1.6 },
                    }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, justifyContent: 'flex-end', alignItems: 'center' }}>
                  {isSaving ? (
                    <CircularProgress size={18} sx={{ mx: 1 }} />
                  ) : (
                    <>
                      <Tooltip title="Cancelar (Esc)">
                        <IconButton size="small" onClick={handleCancelEdit} sx={{ color: 'error.main' }}>
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Confirmar edição (Ctrl+Enter)">
                        <IconButton size="small" onClick={handleConfirmEdit} sx={{ color: 'success.main' }}>
                          <CheckIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ lineHeight: { xs: 1.5, sm: 1.6 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {renderedText}
              </Typography>
            )}
            
            {/* Exibir notas existentes */}
            {hasNotes && (
              <Box sx={{
                mt: { xs: 1, sm: 1.5 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: { xs: 0.75, sm: 1 },
                '#monkee-file-transcript-client &': {
                  display: 'flex !important',
                  flexDirection: 'column !important',
                  alignItems: 'stretch !important',
                },
              }}>
                {annotations.notes.map(note => (
                  <Box
                    key={note.id}
                    sx={{
                      p: { xs: 1, sm: 1.5 },
                      bgcolor: ANNOTATION_COLORS[note.color].bg,
                      border: 'none',
                      borderLeft: `3px solid ${ANNOTATION_COLORS[note.color].border}`,
                      borderRadius: '0 8px 8px 0',
                      fontSize: { xs: 12, sm: 13 },
                      width: '100%',
                      boxSizing: 'border-box',
                      '#monkee-file-transcript-client &': {
                        border: 'none !important',
                        borderLeft: `3px solid ${ANNOTATION_COLORS[note.color].border} !important`,
                        borderRadius: '0 8px 8px 0 !important',
                        width: '100% !important',
                        boxSizing: 'border-box !important',
                        display: 'flex !important',
                        flexDirection: 'column !important',
                      },
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.5,
                      mb: 0.5,
                      '#monkee-file-transcript-client &': {
                        display: 'flex !important',
                        alignItems: 'center !important',
                        justifyContent: 'space-between !important',
                      },
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                        {ANNOTATION_CATEGORIES[note.category].icon} {ANNOTATION_CATEGORIES[note.category].label}
                      </Typography>
                      {canEdit && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, '#monkee-file-transcript-client &': { display: 'flex !important', flexShrink: '0 !important' } }}>
                          <IconButton
                            size="small"
                            onClick={() => onOpenAnnotationDialog(originalIndex, note.id)}
                            sx={{ p: 0.25 }}
                          >
                            <EditNoteIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onDeleteAnnotation(note.id)}
                            sx={{ p: 0.25, color: 'error.main' }}
                          >
                            <DeleteIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{note.text}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Indicadores de anotações - hidden on mobile since actions are always visible */}
          {!isMobile && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              flexShrink: 0,
              width: 72,
              minWidth: 72,
              alignSelf: 'flex-start',
              '#monkee-file-transcript-client &': {
                display: 'flex !important',
                alignItems: 'center !important',
                justifyContent: 'flex-end !important',
                flexShrink: '0 !important',
                width: '72px !important',
                minWidth: '72px !important',
                alignSelf: 'flex-start !important',
              },
              '#monkee-file-transcript-client & svg': {
                verticalAlign: 'middle !important',
                display: 'inline-block !important',
              },
              '#monkee-file-transcript-client & .MuiBadge-root': {
                verticalAlign: 'middle !important',
                display: 'inline-flex !important',
                alignItems: 'center !important',
              },
            }}>
              {hasBookmark && (
                <Tooltip title="Favorito">
                  <BookmarkIcon sx={{ fontSize: 20, color: '#f57c00', display: 'block' }} />
                </Tooltip>
              )}

              {hasNotes && (
                <Tooltip title={`${annotations.notes.length} nota(s)`}>
                  <Badge badgeContent={annotations.notes.length} color="primary" max={9}>
                    <EditNoteIcon sx={{ fontSize: 20, color: '#1976d2', display: 'block' }} />
                  </Badge>
                </Tooltip>
              )}

              {hasHighlight && (
                <Tooltip title="Destacado">
                  <HighlightIcon sx={{ fontSize: 20, color: highlightColor ? ANNOTATION_COLORS[highlightColor].border : '#fbc02d' }} />
                </Tooltip>
              )}
            </Box>
          )}

          {/* Botões de ação - always visible on mobile (touch can't hover), hover on desktop */}
          {canEdit && (
            <Box
              className="annotation-actions"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0, sm: 0.25 },
                opacity: { xs: 1, sm: 0 },
                transition: 'opacity 0.2s',
                flexShrink: 0,
                alignSelf: 'flex-start'
              }}
            >
              {onSegmentSave && !isEditing && !isSaving && (
                <Tooltip title="Editar segmento" disableHoverListener={isMobile}>
                  <IconButton size="small" onClick={handleStartEdit} sx={{ p: { xs: 0.25, sm: 0.5 }, color: 'primary.main' }}>
                    <EditIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  </IconButton>
                </Tooltip>
              )}
              {isSaving && (
                <CircularProgress size={16} sx={{ mx: 0.5 }} />
              )}
              <Tooltip title="Adicionar nota" disableHoverListener={isMobile}>
                <IconButton size="small" onClick={handleAddNote} sx={{ p: { xs: 0.25, sm: 0.5 } }}>
                  <NoteAddIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={hasHighlight ? "Remover destaque" : "Destacar"} disableHoverListener={isMobile}>
                <IconButton
                  size="small"
                  onClick={handleToggleHighlight}
                  sx={{ p: { xs: 0.25, sm: 0.5 }, color: hasHighlight ? '#fbc02d' : undefined }}
                >
                  <HighlightIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={hasBookmark ? "Remover favorito" : "Favoritar"} disableHoverListener={isMobile}>
                <IconButton
                  size="small"
                  onClick={handleToggleBookmark}
                  sx={{ p: { xs: 0.25, sm: 0.5 }, color: hasBookmark ? '#f57c00' : undefined }}
                >
                  {hasBookmark ? <BookmarkIcon sx={{ fontSize: { xs: 16, sm: 18 } }} /> : <BookmarkBorderIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Indicador de confiança */}
          {segment.confidence > 0 && segment.confidence < 0.6 && (
            <Tooltip title={`Confiança na transcrição: ${Math.round(segment.confidence * 100)}%`} disableHoverListener={isMobile}>
              <Chip
                label="⚠️"
                size="small"
                sx={{
                  bgcolor: '#fff3e0',
                  fontSize: { xs: 8, sm: 10 },
                  flexShrink: 0,
                  height: { xs: 18, sm: 24 },
                  // Override WordPress: theme CSS pode sobrescrever font-size do emoji
                  '#monkee-file-transcript-client &': {
                    fontSize: '10px !important',
                    height: '24px !important',
                    '& .MuiChip-label': {
                      fontSize: '10px !important',
                      lineHeight: '1 !important',
                    }
                  }
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Paper>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison for better memoization
  return (
    prevProps.originalIndex === nextProps.originalIndex &&
    prevProps.isSearchMatch === nextProps.isSearchMatch &&
    prevProps.isCurrentMatch === nextProps.isCurrentMatch &&
    prevProps.isHighlighted === nextProps.isHighlighted &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.isSegmentPlaying === nextProps.isSegmentPlaying &&
    prevProps.mediaAvailable === nextProps.mediaAvailable &&
    prevProps.speakerName === nextProps.speakerName &&
    prevProps.canEdit === nextProps.canEdit &&
    prevProps.onSegmentSave === nextProps.onSegmentSave &&
    prevProps.segment.text === nextProps.segment.text &&
    prevProps.renderedText === nextProps.renderedText &&
    prevProps.annotations.notes.length === nextProps.annotations.notes.length &&
    prevProps.annotations.highlights.length === nextProps.annotations.highlights.length &&
    prevProps.annotations.bookmarks.length === nextProps.annotations.bookmarks.length
  )
})

export default TranscriptSegmentItem
